/**
 * Settings Store
 * Manages user preferences and app settings
 */

import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import type { RadarSource } from "@/utils/radarProvider";

export const HomeSectionIds = ["conditions", "hourly", "weekly"] as const;
export type HomeSectionId = (typeof HomeSectionIds)[number];
export const HomeSectionLabels: Record<HomeSectionId, string> = {
  conditions: "Current conditions & radar",
  hourly: "Hourly forecast",
  weekly: "8-day forecast",
};

/** Keep saved home sections unique and include every supported section. */
export function normalizeHomeSectionOrder(value: unknown): HomeSectionId[] {
  const saved = Array.isArray(value)
    ? value.filter((id): id is HomeSectionId => HomeSectionIds.includes(id))
    : [];
  return [...new Set([...saved, ...HomeSectionIds])];
}

export type TemperatureUnit = "Imperial" | "Metric";
export type ThemeMode = "dark" | "light" | "system";
export type BorderColorId =
  | "blue"
  | "purple"
  | "teal"
  | "rose"
  | "amber"
  | "pink";
export const BorderGradientIds = [
  "ocean",
  "sunset",
  "aurora",
  "ember",
  "lavender",
  "mint",
  "neon",
  "rose",
  "rainbow",
] as const;
export type SelectionBorderStyle = "solid" | "gradient";
export type BorderGradientId = (typeof BorderGradientIds)[number];

// Solid border colors
export const BorderColors: Record<
  BorderColorId,
  { primary: string; name: string }
> = {
  blue: { primary: "#2189FF", name: "Blue" }, // Owl face in AppIcon.svg
  purple: { primary: "#8B5CF6", name: "Purple" },
  teal: { primary: "#14B8A6", name: "Teal" },
  rose: { primary: "#F43F5E", name: "Rose" },
  amber: { primary: "#F59E0B", name: "Amber" },
  pink: { primary: "#EC4899", name: "Pink" },
};

export interface MapPreferences {
  radarAnimationSpeed: number;
  radarOpacity: number;
  radarSourceOverride?: RadarSource;
  showRadarLayer: boolean;
  showWindLayer: boolean;
  windParticleOpacity: number;
  unit: TemperatureUnit;
}

const STORAGE_KEY = "rainowls-settings";

export interface Settings {
  homeSectionOrder: HomeSectionId[];
  temperatureUnit: TemperatureUnit;
  themeMode: ThemeMode;
  radarAnimationSpeed: number;
  radarOpacity: number; // 0–1, default 0.5
  showRadarLayer: boolean;
  showWindLayer: boolean;
  windParticleOpacity: number; // 0–1, default 0.95
  autoRefreshInterval: number; // in minutes, 0 = disabled
  selectionBorderStyle: SelectionBorderStyle;
  borderColor: BorderColorId;
  borderGradient: BorderGradientId;
}

const defaultSettings: Settings = {
  homeSectionOrder: [...HomeSectionIds],
  temperatureUnit: "Imperial",
  themeMode: "dark",
  radarAnimationSpeed: 250,
  radarOpacity: 0.5,
  showRadarLayer: true,
  showWindLayer: true,
  windParticleOpacity: 0.95,
  autoRefreshInterval: 15,
  selectionBorderStyle: "gradient",
  borderColor: "blue",
  borderGradient: "ocean",
};

/** Validate untrusted localStorage before it reaches reactive consumers. */
export function normalizeSettings(value: unknown): Settings {
  const raw =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};

  // Keep numeric settings finite and inside their supported ranges.
  const number = (key: keyof Settings, low: number, high: number) => {
    const value = raw[key];
    return typeof value === "number" && Number.isFinite(value)
      ? Math.min(high, Math.max(low, value))
      : (defaultSettings[key] as number);
  };

  // Preserve the user's previous accent choice as their solid-border choice.
  const savedColor = raw.borderColor ?? raw.accentColor;
  return {
    homeSectionOrder: normalizeHomeSectionOrder(raw.homeSectionOrder),
    selectionBorderStyle:
      raw.selectionBorderStyle === "solid" ? "solid" : "gradient",
    temperatureUnit: raw.temperatureUnit === "Metric" ? "Metric" : "Imperial",
    themeMode: "dark", // Light appearance is paused until the icon refresh.
    radarAnimationSpeed: number("radarAnimationSpeed", 100, 2000),
    radarOpacity: number("radarOpacity", 0, 1),
    showRadarLayer:
      typeof raw.showRadarLayer === "boolean"
        ? raw.showRadarLayer
        : defaultSettings.showRadarLayer,
    showWindLayer:
      typeof raw.showWindLayer === "boolean"
        ? raw.showWindLayer
        : defaultSettings.showWindLayer,
    windParticleOpacity: number("windParticleOpacity", 0, 1),
    autoRefreshInterval:
      typeof raw.autoRefreshInterval === "number" &&
      [0, 15, 30, 45, 60].includes(raw.autoRefreshInterval)
        ? raw.autoRefreshInterval
        : defaultSettings.autoRefreshInterval,
    borderGradient: BorderGradientIds.includes(
      raw.borderGradient as BorderGradientId,
    )
      ? (raw.borderGradient as BorderGradientId)
      : defaultSettings.borderGradient,
    borderColor:
      typeof savedColor === "string" &&
      Object.prototype.hasOwnProperty.call(BorderColors, savedColor)
        ? (savedColor as BorderColorId)
        : defaultSettings.borderColor,
  };
}

/** Apply the resolved theme at the DOM boundary; Pinia owns the preference. */
function applyTheme(isDark: boolean) {
  if (typeof document === "undefined") return;
  if (isDark) document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", isDark ? "#12181f" : "#f3f4f6");
}

export const useSettingsStore = defineStore("settings", () => {
  const homeSectionOrder = ref<HomeSectionId[]>([...HomeSectionIds]);
  const temperatureUnit = ref<TemperatureUnit>(defaultSettings.temperatureUnit);
  const themeMode = ref<ThemeMode>(defaultSettings.themeMode);
  const radarAnimationSpeed = ref(defaultSettings.radarAnimationSpeed);
  const radarOpacity = ref(defaultSettings.radarOpacity);
  const showRadarLayer = ref(defaultSettings.showRadarLayer);
  const showWindLayer = ref(defaultSettings.showWindLayer);
  const windParticleOpacity = ref(defaultSettings.windParticleOpacity);
  const autoRefreshInterval = ref(defaultSettings.autoRefreshInterval);
  const selectionBorderStyle = ref<SelectionBorderStyle>(
    defaultSettings.selectionBorderStyle,
  );
  const borderColor = ref<BorderColorId>(defaultSettings.borderColor);
  const borderGradient = ref<BorderGradientId>(defaultSettings.borderGradient);

  // Local radar comparisons are session-only and never enter saved settings.
  const devRadarSource = ref<RadarSource | undefined>(undefined);

  // Runtime overrides used by weather-flow tests; never load retired console preferences.
  const usePrivateOpenMeteo = ref(true); // Default to RainOwls primary with public Open-Meteo fallback
  const useMockData = ref(false);
  const mockWeatherCondition = ref<string>("rain");

  const mapPreferences = computed<MapPreferences>(() => ({
    radarAnimationSpeed: radarAnimationSpeed.value,
    radarOpacity: radarOpacity.value,
    radarSourceOverride: devRadarSource.value,
    showRadarLayer: showRadarLayer.value,
    showWindLayer: showWindLayer.value,
    windParticleOpacity: windParticleOpacity.value,
    unit: temperatureUnit.value,
  }));

  // Temporary product policy: all renderers stay dark, including saved Light/System users.
  const isDarkMode = computed(() => true);

  /** Keep home sections complete after a drag reorder or saved-setting load. */
  function setHomeSectionOrder(order: readonly HomeSectionId[]) {
    homeSectionOrder.value = normalizeHomeSectionOrder(order);
  }

  /** Set the display unit used by weather projections. */
  function setTemperatureUnit(unit: TemperatureUnit): void {
    temperatureUnit.value = unit;
  }

  /** Preserve the current dark-only appearance policy. */
  function setThemeMode(_mode: ThemeMode): void {
    themeMode.value = "dark";
  }

  /** Clamp radar opacity to the supported range. */
  function setRadarOpacity(opacity: number): void {
    radarOpacity.value = Number.isFinite(opacity)
      ? Math.max(0, Math.min(1, opacity))
      : 0.5;
  }

  /** Select one radar provider for local comparison without changing saved preferences. */
  function setDevRadarSource(source: RadarSource | undefined): void {
    devRadarSource.value = source;
  }

  /** Clamp wind particle opacity to the supported range. */
  function setWindParticleOpacity(opacity: number): void {
    windParticleOpacity.value = Number.isFinite(opacity)
      ? Math.max(0, Math.min(1, opacity))
      : 0.5;
  }

  /** Apply only supported automatic-refresh intervals. */
  function setAutoRefreshInterval(minutes: number): void {
    autoRefreshInterval.value = normalizeSettings({
      autoRefreshInterval: minutes,
    }).autoRefreshInterval;
  }

  /** Set a supported selection-border style. */
  function setSelectionBorderStyle(style: SelectionBorderStyle): void {
    selectionBorderStyle.value = style === "solid" ? "solid" : "gradient";
  }

  /** Select a shared solid border color. */
  function setBorderColor(color: BorderColorId): void {
    borderColor.value = color;
  }

  /** Validate and select a shared border gradient. */
  function setBorderGradient(gradient: BorderGradientId): void {
    borderGradient.value = normalizeSettings({
      borderGradient: gradient,
    }).borderGradient;
  }

  /** Restore user preferences while clearing session-only radar overrides. */
  function resetToDefaults(): void {
    homeSectionOrder.value = [...HomeSectionIds];
    temperatureUnit.value = defaultSettings.temperatureUnit;
    themeMode.value = defaultSettings.themeMode;
    radarAnimationSpeed.value = defaultSettings.radarAnimationSpeed;
    radarOpacity.value = defaultSettings.radarOpacity;
    showRadarLayer.value = defaultSettings.showRadarLayer;
    showWindLayer.value = defaultSettings.showWindLayer;
    windParticleOpacity.value = defaultSettings.windParticleOpacity;
    autoRefreshInterval.value = defaultSettings.autoRefreshInterval;
    selectionBorderStyle.value = defaultSettings.selectionBorderStyle;
    borderColor.value = defaultSettings.borderColor;
    borderGradient.value = defaultSettings.borderGradient;
    devRadarSource.value = undefined;
  }

  /** Read and normalize saved preferences before exposing them to the app. */
  function loadSettings(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = normalizeSettings(JSON.parse(stored));
        homeSectionOrder.value = parsed.homeSectionOrder;
        temperatureUnit.value = parsed.temperatureUnit;
        themeMode.value = parsed.themeMode;
        radarAnimationSpeed.value = parsed.radarAnimationSpeed;
        radarOpacity.value = parsed.radarOpacity;
        showRadarLayer.value = parsed.showRadarLayer;
        showWindLayer.value = parsed.showWindLayer;
        windParticleOpacity.value = parsed.windParticleOpacity;
        autoRefreshInterval.value = parsed.autoRefreshInterval;
        selectionBorderStyle.value = parsed.selectionBorderStyle;
        borderColor.value = parsed.borderColor;
        borderGradient.value = parsed.borderGradient;
      }
    } catch {
      console.error("Failed to load settings");
    }
  }

  /** Persist user settings, excluding session-only development overrides. */
  function persistSettings(): void {
    try {
      const settings: Settings = {
        homeSectionOrder: homeSectionOrder.value,
        temperatureUnit: temperatureUnit.value,
        themeMode: themeMode.value,
        radarAnimationSpeed: radarAnimationSpeed.value,
        radarOpacity: radarOpacity.value,
        showRadarLayer: showRadarLayer.value,
        showWindLayer: showWindLayer.value,
        windParticleOpacity: windParticleOpacity.value,
        autoRefreshInterval: autoRefreshInterval.value,
        selectionBorderStyle: selectionBorderStyle.value,
        borderColor: borderColor.value,
        borderGradient: borderGradient.value,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      console.error("Failed to persist settings");
    }
  }

  // Watch for changes and persist
  watch(
    [
      homeSectionOrder,
      temperatureUnit,
      themeMode,
      radarAnimationSpeed,
      radarOpacity,
      showRadarLayer,
      showWindLayer,
      windParticleOpacity,
      autoRefreshInterval,
      selectionBorderStyle,
      borderColor,
      borderGradient,
    ],
    () => {
      persistSettings();
    },
  );

  // Initialize settings from storage
  loadSettings();

  // Apply theme on initialization
  watch(isDarkMode, applyTheme, { immediate: true, flush: "sync" });

  return {
    // State
    homeSectionOrder,
    setHomeSectionOrder,
    temperatureUnit,
    themeMode,
    radarAnimationSpeed,
    radarOpacity,
    showRadarLayer,
    showWindLayer,
    windParticleOpacity,
    autoRefreshInterval,
    selectionBorderStyle,
    borderColor,
    borderGradient,
    devRadarSource,

    // Runtime data-source overrides
    usePrivateOpenMeteo,
    useMockData,
    mockWeatherCondition,

    mapPreferences,
    // Computed
    isDarkMode,

    // Actions
    setTemperatureUnit,
    setThemeMode,
    setRadarOpacity,
    setDevRadarSource,
    setWindParticleOpacity,
    setAutoRefreshInterval,
    setSelectionBorderStyle,
    setBorderColor,
    setBorderGradient,
    resetToDefaults,
    loadSettings,
  };
});
